import {
  AGENT_JUDGE_CATEGORICAL_MANAGER,
  AGENT_JUDGE_MANAGER,
  AGENT_JUDGE_NUMERICAL_MANAGER,
  validateAggregateMetadata,
  validateStrictLogicMetadata,
  type AggregateMetadata,
  type StrictLogicMetadata,
} from "@/metrics/registry"

import type { MetricRecordData } from "@/types/metrics/contracts"

/**
 * LLM judge managers whose evaluation prompt (`question`) must be filled in for
 * the metric to produce meaningful output. Text and audio variants both render
 * the QuestionTextarea, so both require it.
 */
const QUESTION_REQUIRED_MANAGERS = new Set<string>([
  AGENT_JUDGE_MANAGER,
  AGENT_JUDGE_CATEGORICAL_MANAGER,
  AGENT_JUDGE_NUMERICAL_MANAGER,
  "manager_llm_binary",
  "manager_default_categorical",
  "manager_numerical_llm_judge",
  "manager_audio_llm_judge",
  "manager_audio_llm_binary",
  "manager_audio_llm_categorical",
  "manager_audio_llm_numerical",
])

/**
 * Categorical LLM judges (text + audio) store their options as a comma-joined
 * string in `additional_information.value`. The backend needs at least two.
 */
const CATEGORY_REQUIRED_MANAGERS = new Set<string>([
  "manager_default_categorical",
  "manager_audio_llm_categorical",
])

const AGENT_JUDGE_MANAGERS = new Set<string>([
  AGENT_JUDGE_MANAGER,
  AGENT_JUDGE_CATEGORICAL_MANAGER,
  AGENT_JUDGE_NUMERICAL_MANAGER,
])

function isBlank(value: unknown): boolean {
  return typeof value !== "string" || value.trim().length === 0
}

function countCategories(value: string | undefined | null): number {
  return (value ?? "")
    .split(",")
    .map((entry) => entry.trim())
    .filter(Boolean).length
}

/**
 * Returns a human-readable error naming the first unfilled required field for a
 * metric, or null when every required field is populated.
 *
 * This is the single source of truth behind BOTH the red `*` markers rendered
 * on config field labels and the disabled state of the Create/Save button, so
 * the two never drift. Audio preset metrics (loudness, artifacts, etc.) seed
 * working defaults and therefore have no required fields here.
 */
export function getRequiredFieldError(
  metric: Pick<
    MetricRecordData,
    "metric_manager" | "metric_metadata" | "metric_display_name"
  >,
): string | null {
  const manager = metric.metric_manager
  const metadata = metric.metric_metadata ?? {}

  if (isBlank(metric.metric_display_name)) {
    return "A metric name is required."
  }

  if (QUESTION_REQUIRED_MANAGERS.has(manager) && isBlank(metadata.question)) {
    return "A metric prompt is required."
  }

  if (
    AGENT_JUDGE_MANAGERS.has(manager) &&
    Array.isArray(metadata.enabled_tools) &&
    metadata.enabled_tools.length === 0
  ) {
    return "Enable at least one tool."
  }

  if (
    CATEGORY_REQUIRED_MANAGERS.has(manager) &&
    countCategories(metadata.additional_information?.value) < 2
  ) {
    return "Add at least two categories."
  }

  if (
    manager === AGENT_JUDGE_CATEGORICAL_MANAGER &&
    (metadata.categories?.filter((category) => category.trim().length > 0)
      .length ?? 0) < 2
  ) {
    return "Add at least two categories."
  }

  switch (manager) {
    case "manager_composite_evaluation": {
      const source = metadata.criteria_source ?? "test_case"
      if (source === "metric_metadata") {
        const hasCriterion = (metadata.criteria ?? []).some(
          (criterion) => !isBlank(criterion),
        )
        if (!hasCriterion) {
          return "At least one criterion is required."
        }
      } else if (
        source === "test_case_attribute" &&
        isBlank(metadata.criteria_path)
      ) {
        return "An attribute path is required."
      }
      return null
    }

    case "manager_metadata_field_metric":
      return isBlank(metadata.metadata_field_key)
        ? "A field key is required."
        : null

    case "manager_transcript_regex_match":
      if (isBlank(metadata.regex_pattern)) {
        return "A regex pattern is required."
      }
      if (isBlank(metadata.role)) {
        return "A role is required."
      }
      return null

    case "manager_api_state":
      if (isBlank(metadata.api_url)) {
        return "An API URL is required."
      }
      if (isBlank(metadata.expected_body)) {
        return "An expected body is required."
      }
      return null

    case "manager_custom_trace":
      return isBlank(metadata.span_name) ? "A span name is required." : null

    case "manager_sql_float":
      return isBlank(metadata.sql_query) ? "A SQL query is required." : null

    case "manager_rubric_decision_tree":
      if (isBlank(metadata.decision_tree_prompt)) {
        return "A decision tree prompt is required."
      }
      if (!(metadata.tree_definition ?? []).length) {
        return "A decision tree is required."
      }
      return null

    case "manager_ivr_flow_adherence":
      return metadata.ivr_flow?.nodes?.length
        ? null
        : "An IVR flow is required."

    case "manager_words_per_message_with_threshold":
      if (isBlank(metadata.operator)) {
        return "An operator is required."
      }
      if (typeof metadata.threshold !== "number") {
        return "A word count threshold is required."
      }
      return null

    case "manager_custom_audio_frequency":
      if (metadata.frequency_threshold == null) {
        return "Frequency Threshold is required."
      }
      if (metadata.percent_above_threshold == null) {
        return "Display Cutoff is required."
      }
      return null

    case "manager_custom_end_reason":
      return metadata.success_end_reasons?.length
        ? null
        : "At least one Success End Reason must be selected."

    case "manager_custom_agent_needs_reprompting":
      return metadata.min_silence_gap_seconds == null
        ? "Min Silence Gap is required."
        : null

    case "manager_custom_audio_sentiment":
      if (!metadata.success_sentiments?.length) {
        return "At least one Success Sentiment must be selected."
      }
      if (metadata.percent_above == null) {
        return "Percent Above is required."
      }
      return null

    case "manager_custom_agent_fails_to_respond":
      return metadata.max_silence_duration_seconds == null
        ? "Max Silence Duration is required."
        : null

    case "manager_derived_aggregate":
      return validateAggregateMetadata(metadata as unknown as AggregateMetadata)

    case "manager_derived_strict_logic":
      return validateStrictLogicMetadata(
        metadata as unknown as StrictLogicMetadata,
      )

    default:
      return null
  }
}
